<template>
  <div class="announcement-bar__container">
    <div
      ref="section"
      class="announcement-bar critical-component-clear"
      :class="{
        [$class('active')]: isLoaded,
        'inspector-mode': inspectorMode,
      }"
    >
      <div
        :id="`section-title-${liquid.sectionId}`"
        class="visually-hidden"
      />

      <div
        ref="carousel"
        class="announcement-bar__wrapper splide"
        js-carousel="announcement-bar"
      >
        <div class="splide__track">
          <div class="splide__list">
            <div
              v-for="(announcement) of announcements"
              :key="announcement.id"
              class="announcement-bar__item splide__slide"
              :class="{
                'announcement-bar__item--noArrows': isMobile && disableArrows
                  || !isMobile && disableArrowsDesktop,
              }"
              v-html="
                isMobile && announcement.mobileText ? announcement.mobileText : announcement.text
              "
            />
          </div>
        </div>

        <div
          v-if="announcements.length > 1 &&
            !(isMobile && disableArrows) &&
            !(!isMobile && disableArrowsDesktop)"
          v-show="carousel"
          class="splide__arrows announcement-bar-arrows"
        >
          <button
            class="splide__arrow splide__arrow--prev"
            type="button"
            :aria-label="$string('accessibility.sign_posts.carousel.previous')"
          >
            <icon-left />
          </button>

          <button
            class="splide__arrow splide__arrow--next"
            type="button"
            :aria-label="$string('accessibility.sign_posts.carousel.next')"
          >
            <icon-right />
          </button>
        </div>
      </div>
    </div>

    <button
      class="announcement-bar__close icon-button"
      type="button"
      :aria-label="$string('accessibility.close')"
      @click="disableAnnouncementBar"
    >
      <icon-close />
    </button>
  </div>
</template>

<script>

/**
 * Vue: Announcement bar (announcement-bar)
 * -----------------------------------------------------------------------------
 * Announcement Bar used below the site header.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Array} announcements - Section blocks containing the announcements.
 * @param {Number} autoplayInterval - Interval in seconds for the carousel.
 *
 */
import { mapActions, mapState } from 'vuex'

import IconLeft from '~icons/direction/arrow/left.svg'
import IconRight from '~icons/direction/arrow/right.svg'
import IconClose from '~icons/misc/close.svg'

import Carousel from '~/api/carousel'
import { values } from '~/config/breakpoints'

export default {
  name: 'AnnouncementBar',

  components: {
    IconLeft,
    IconRight,
    IconClose,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },

    announcements: {
      type: Array,
      default: () => [],
    },

    autoplayInterval: {
      type: Number,
      default: () => 0,
    },

    disableArrows: {
      type: Boolean,
      default: false,
    },

    disableArrowsDesktop: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isDisable: false,
      mediaHeight: 0,
      isLoaded: false,
      inspector: false,
      carousel: false,
      carouselConfig: {
        arrows: true,
        type: 'loop',
        speed: 800,
        drag: window.innerWidth <= values.m,
        autoplay: this.autoplayInterval > 0,
        interval: `${this.autoplayInterval}000`,
      },
      inspectorMode: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    if (this.$refs.section && !this.isLoaded) {
      this.isLoaded = true
    }

    if (this.$refs.carousel) {
      this.initCarousel()
    }

    const sectionHeight = this.$refs.section.getBoundingClientRect().height

    window.addEventListener('load', () => {
      setTimeout(() => {
        document.documentElement.style.setProperty(
          '--announcement-bar-height',
          `${Math.max(0, sectionHeight - Math.abs(this.$refs.section.getBoundingClientRect().top))}px`,
        )

        this.isLoaded = true
      }, 700)
    }, false)

    window.visualViewport.addEventListener('resize', () => {
      if (this.isDisable) {
        return
      }
      document.documentElement.style.setProperty(
        '--announcement-bar-height',
        `${Math.max(0, sectionHeight - Math.abs(this.$refs.section.getBoundingClientRect().top))}px`,
      )
    })

    // set announcement bar height on scroll
    window.addEventListener('scroll', () => {
      if (this.isDisable) {
        return
      }

      document.documentElement.style.setProperty(
        '--announcement-bar-height',
        `${Math.max(0, sectionHeight - Math.abs(this.$refs.section.getBoundingClientRect().top))}px`,
      )
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      updateCartAttributes: 'cart/updateCartAttributes',
    }),

    /**
     * Initialize the carousel.
     */
    initCarousel() {
      const slides = this.$refs.carousel.querySelectorAll('.splide__slide')

      if (slides.length < 2) {
        return
      }

      this.carousel = Carousel(
        `[js-carousel="announcement-bar"]`,
        {
          ...this.carouselConfig,
          arrows: this.announcements.length > 1 && !(
            (this.isMobile && this.disableArrows) ||
            (!this.isMobile && this.disableArrowsDesktop)),
        },
      )

      this.carousel.mount()
    },

    disableAnnouncementBar() {
      this.updateCartAttributes({
        attributes: {
          announcement_bar_disabled: 'true',
          announcement_bar_disabled_at: new Date().toISOString(),
        },
      })
      this.isLoaded = false
      this.isDisable = true
      this.$refs.section.style.display = 'none'
      document.documentElement.style.setProperty('--announcement-bar-height', '0px')
    },

    /**
     * Shopify inspector activate event.
     * - Disable transform.
     */
    shopifyInspectorActivate() {
      this.inspectorMode = true
    },

    /**
     * Shopify inspector deactivate event.
     */
    shopifyInspectorDeactivate() {
      this.inspectorMode = false
    },
  },
}
</script>

<style lang="scss">
@import './announcement-bar';
</style>
